import type { FaceKey } from '@repo/api-contracts';
import { FACE_LABELS } from '@repo/design-viewer';
import type { ReactElement } from 'react';
import { cn } from '@/shared/ui';

interface FaceSwitcherOption {
  readonly face: FaceKey;
  /** 이 면에 레이어가 있는지 — 알약 글자 옆 라임 점으로 표시한다. */
  readonly hasLayers: boolean;
}

interface FaceSwitcherProps {
  readonly faces: readonly FaceSwitcherOption[];
  readonly value: FaceKey;
  readonly onChange: (face: FaceKey) => void;
}

/**
 * 캔버스 아래 가운데에 떠 있는 인쇄 면 알약 — 고른 면은 흑연, 나머지는 흰색. 디자인을 넣은 면은 글자 옆 라임 점.
 * 편집영역 정의에 있는 면만 받고, 면이 하나뿐이면 고를 것이 없어 아무것도 그리지 않는다.
 */
export const FaceSwitcher = ({ faces, value, onChange }: FaceSwitcherProps): ReactElement | null =>
  faces.length < 2 ? null : (
    <nav
      aria-label="인쇄 면"
      className="flex items-center gap-1 rounded-full bg-background/95 p-1 shadow-md ring-1 ring-foreground/10"
    >
      {faces.map((option) => {
        const isSelected = option.face === value;
        return (
          <button
            key={option.face}
            type="button"
            aria-pressed={isSelected}
            aria-label={`${FACE_LABELS[option.face]}${option.hasLayers ? ' (디자인 있음)' : ''}`}
            onClick={() => onChange(option.face)}
            className={cn(
              'flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-4 text-[13px] font-semibold whitespace-nowrap transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isSelected ? 'bg-foreground text-background' : 'text-foreground hover:bg-muted',
            )}
          >
            {FACE_LABELS[option.face]}
            {option.hasLayers && (
              <span
                aria-hidden
                className={cn(
                  'size-1.5 rounded-full bg-brand-lime',
                  isSelected ? 'ring-1 ring-background/60' : 'ring-1 ring-foreground/40',
                )}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
